'use client';

import Image from 'next/image';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { AnimatedBorderButton } from '@/components/platform/buttons';

interface EmptyStateWithActionProps {
  title: string;
  message: string;
  buttonText: string;
  onButtonClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  secondaryAction?: {
    text: string;
    onClick: () => void;
  };
}

export default function EmptyStateWithAction({
  title,
  message,
  buttonText,
  onButtonClick,
  loading = false,
  disabled = false,
  secondaryAction,
}: EmptyStateWithActionProps) {
  const { darkMode } = useDarkMode();

  return (
    <div className="relative flex min-h-[calc(100vh-12rem)] items-center justify-center overflow-hidden">
      <div className="relative z-10 max-w-md text-center">
        <h2
          className={`mb-4 text-2xl font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}
        >
          {title}
        </h2>
        <p className={`mb-8 text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {message}
        </p>
        <div className="flex flex-col items-center gap-5">
          {/* Graphic anchored to the BUTTON itself (centered on it), so the
              converging lines align with the button at every screen size. */}
          <div className="relative">
            <div className="pointer-events-none absolute top-1/2 left-1/2 -z-10 w-[min(92vw,800px)] -translate-x-1/2 -translate-y-1/2">
              <Image
                src="/animated-welcome-graphic.svg"
                alt=""
                width={800}
                height={600}
                className="w-full"
                priority
              />
            </div>
            <AnimatedBorderButton onClick={onButtonClick} loading={loading} disabled={disabled}>
              <span style={{ color: 'white', fontSize: '0.875rem', fontWeight: 600 }}>
                {buttonText}
              </span>
            </AnimatedBorderButton>
          </div>
          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              className={`text-sm font-medium ${
                darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'
              } transition-colors`}
            >
              {secondaryAction.text}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
